import dayjs from 'dayjs';
import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DeliveryFlowList from '../../components/selfcare/DeliveryFlow/DeliveryFlowList';
import {Details} from '../../components/selfcare/Details';
import HelpArticlesAccount from '../../components/selfcare/HelpArticlesAccount';
import Icon from '../../components/selfcare/Icon';
import OverviewStaticNotifications from '../../components/selfcare/OverviewStaticNotifications';
import PermissionBanner from '../../components/selfcare/PermissionBanner';
import PermissionPopup from '../../components/selfcare/PermissionPopup';
import SelfcarePageContent from '../../components/selfcare/SelfcarePageContent';
import {useDetailsState} from '../../components/selfcare/useDetailsState.tsx';
import {useCustomer} from '../../layouts/SelfcareLayout';
import {formatAmountWithCurrencyOverPeriod, formatCardExpiration, formatDate} from '../../util/formatter';
import {dateCompare, isDateMaxDate} from '../../util/helpers';
import {selfcareCheckPopupEligible} from '../../util/request.ts';
import {
    ChargePeriod,
    Customer,
    CUSTOMER_STATUS,
    CUSTOMER_TYPES,
    PRODUCT_IDS,
    PRODUCT_STATUS,
    DiscountInstance,
    DiscountInstanceMonetary,
    DiscountInstancePercentage,
    PermissionOption,
    ProductInstance,
} from '../../util/types';
import Routing from "../../Routing.ts";
import now from "../../util/now.ts";
import IconCreditcard from "../../components/icons/IconCreditcard.tsx";
import setTitleAndMetaDescription from "../../components/setTitleAndMetaDescription.tsx";

export default function OverviewPage() {
    setTitleAndMetaDescription("Overblik - Mit Hiper", "Her kan du se dit abonnement, dine produkter og betalingsoplysninger.");

    const {customer} = useCustomer();
    const [showPermissionPopup, setShowPermissionPopup] = useState(false);
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        const checkConditions = async () => {
            const popupShown = sessionStorage.getItem('popupShown');
            if (popupShown) {
                return;
            }
            const response = await selfcareCheckPopupEligible(customer.customer_id);
            const popupEligible = response.response.data;
            const marketingPermissions: PermissionOption[] = Object
                .keys(customer.customer_permission)
                .filter(key => key.startsWith('contact_permission_Marketing'))
                .map(key => customer.customer_permission[key as keyof typeof customer.customer_permission])
                .filter(
                    permission => typeof permission === 'object'
                                  && permission !== null,
                ) as PermissionOption[];
            const hasUnsetPermission = marketingPermissions.some(
                (permission: PermissionOption) => !permission.is_action_set,
            );

            if (popupEligible && hasUnsetPermission) {
                setShowPermissionPopup(true);
                sessionStorage.setItem('popupShown', 'true');
            } else if (hasUnsetPermission) {
                setShowBanner(true);
            } else {
                setShowPermissionPopup(false);
                setShowBanner(false);
            }
        };

        checkConditions();
    }, [customer]);

    const closePermissionPopup = () => {
        setShowPermissionPopup(false);
    };

    const handleBannerClick = () => {
        setShowPermissionPopup(true);
        setShowBanner(false);
    };

    return <>
        {showBanner && <PermissionBanner onClick={handleBannerClick}/>}
        {showPermissionPopup && <PermissionPopup onClose={closePermissionPopup} customer_id={customer.customer_id}/>}
        <CustomerClosed customer={customer}/>
        <SelfcarePageContent className="selfcare-page-content--wide selfcare-page-overview">
            <OverviewStaticNotifications customer={customer}/>
            <OverviewAccount customer={customer}/>
        </SelfcarePageContent>
        <DeliveryFlowList/>
        <hr/>
        <HelpArticlesAccount/>
    </>;
}

function OverviewAccount({customer}: { customer: Customer }) {
    const extenderProducts = customer.product_instances.filter(productInstance => isExtenderProduct(productInstance));
    return <section className="selfcare-overview-account">
        <h1 className="heading heading--big">Mit abonnement</h1>
        <div className="account-container">
            <CurrentProductList customer={customer} />
            <ClosedProductList customer={customer} />

            {extenderProducts.length > 0 && <ExtenderProducts customer={customer} productInstances={extenderProducts}/>}

            <CustomerPaymentDetails customer={customer}/>
        </div>
    </section>;
}

function productsWithoutExtenders(customer: Customer) {
    return customer.product_instances.filter(productInstance => !isExtenderProduct(productInstance));
}

function currentProducts(customer: Customer) {
    return productsWithoutExtenders(customer).filter(
        productInstance => [
            PRODUCT_STATUS.ACTIVE,
            PRODUCT_STATUS.PENDING,
        ].includes(productInstance.product_instance_status),
    );
}

function closedProducts(customer: Customer) {
    return productsWithoutExtenders(customer).filter(
        productInstance => (
            isProductNotIpProduct(productInstance)
            && (isProductFinishedWithDate(productInstance) || isProductCancelledWithDate(productInstance))
        ));
}

function CurrentProductList({customer}: {customer: Customer}) {
    const products = currentProducts(customer);
    products.sort(
        (a, b) => dateCompare(
            a.product_instance_status_pending_date as string,
            b.product_instance_status_pending_date as string,
        ),
    ).reverse();
    return products.map(
        (productInstance: ProductInstance, index: number) =>
            <Product key={index} customer={customer} productInstance={productInstance}/>,
    );
}

function Product({customer, productInstance}: { customer: Customer, productInstance: ProductInstance }) {
    return <div className="product">
        <p className="title">{productInstance.product.product_display_name}</p>
        <OnlineWidget productInstance={productInstance}/>
        <ProductPricingDetails customer={customer} productInstance={productInstance}/>
    </div>;
}

function ClosedProductList({customer}: { customer: Customer }) {
    const [showClosedProducts, setShowClosedProducts] = useState(false);
    const hasCurrentProducts = currentProducts(customer).length > 0;
    const products = closedProducts(customer);
    const hasClosedProducts = products.length > 0;
    const toggleClosedProducts = () => setShowClosedProducts((prevState) => !prevState);
    products.sort(
        (a, b) => dateCompare(
            a.product_instance_status_pending_finish_date as string,
            b.product_instance_status_pending_finish_date as string
        )
    ).reverse();

    if (hasCurrentProducts && hasClosedProducts) {
        return <div className={'product-history-container'}>
            <p onClick={toggleClosedProducts} className="toggle-open">
                Vis produkthistorik
                <span className={`arrow${showClosedProducts ? ' arrow-up' : ' arrow-down'}`}></span>
            </p>
            <div className={`product-history${!showClosedProducts ? ' product-history--hidden' : ''}`}>
                {products.map(
                    (productInstance: ProductInstance, index: number) =>
                        <Product key={index} customer={customer} productInstance={productInstance}/>,
                )}
            </div>
        </div>;
    } else if (hasCurrentProducts === false && hasClosedProducts) {
        return products.map(
            (productInstance: ProductInstance, index: number) =>
                <Product key={index} customer={customer} productInstance={productInstance}/>,
        );
    } else {
        return;
    }
}

function ExtenderProducts({customer, productInstances}: {customer: Customer, productInstances: ProductInstance[]}) {
    return <div className="product">
        <p className="title">{productInstances[0].product.product_display_name} - {productInstances.length} stk.</p>
        <ExtenderProductPricingDetails customer={customer} productInstances={productInstances}/>
    </div>;
}

function OnlineWidget({productInstance}: {productInstance: ProductInstance}) {
    const shouldDisplayOnlineWidget: boolean = isProductNotIpProduct(productInstance);

    if (!shouldDisplayOnlineWidget) {
        return;
    }

    return <>
        <OnlineText productInstance={productInstance}/>
        {productInstance.lan_port &&
         productInstance.product_instance_status === PRODUCT_STATUS.ACTIVE &&
         <p className="sub">
             Aktiv LAN-port på fiberboks: <strong>LAN{productInstance.lan_port}</strong>
         </p>}
    </>
}

function OnlineText({productInstance}: { productInstance: ProductInstance }) {
    const activeDate = formatDate(productInstance.product_instance_status_active_date as string);
    const finishDate = formatDate(productInstance.product_instance_status_finish_date as string);
    const pendingFinishDate = formatDate(productInstance.product_instance_status_pending_finish_date as string);
    const cancelDate = formatDate(productInstance.product_instance_status_cancel_date as string);

    const onlineDate: React.JSX.Element = <strong>{activeDate}</strong>;
    const isOnlineDatePassed: boolean = dayjs(productInstance.product_instance_status_active_date).isBefore(now);

    if (isProductFinishedWithDate(productInstance)) {
        return <>
            <p className="sub">
                Aktivt d. <strong>{activeDate} - {finishDate}</strong>
            </p>
            <p className="sub">Opsigelse registreret d. <strong>{pendingFinishDate}</strong></p>
        </>;
    }

    if (isProductCancelledWithDate(productInstance)) {
        return <p className="sub">Annulleret d. <strong>{cancelDate}</strong></p>;
    }

    if (isOnlineDatePassed && isTheProductActiveKnown(productInstance)) {
        return <>
            <p className="sub">Aktiv siden d. <strong>{onlineDate}</strong></p>
            {isPendingFinished(productInstance) &&
                <p className="sub">Opsigelse registreret d. <strong>{pendingFinishDate}</strong></p>
            }
        </>;
    }

    if (isTheProductActiveKnown(productInstance)) {
        return <p className="sub">Klar til brug d. <strong>{onlineDate}</strong></p>;
    }

    return;
}

function CustomerPaymentDetails({customer}: { customer: Customer }) {
    if (isCustomerClosed(customer) ) {
        return;
    }
    return <div className="payment">
        <p className="title">
            Betaling
        </p>

        {isLargeCustomer(customer) ?
            <p>
                Dit abonnement bliver betalt af din arbejdsgiver: <strong>{customer.customer_type.parent_company_name}</strong>
            </p>
            :
            <>{customer.customer_context.finances.has_card && customer.customer_context.finances.card ?
                <><p className="icon-with-text">
                    <Icon component={IconCreditcard}/>
                    <span className="text">Kortnummer: <span
                        className="bullets"
                    >&bull;&bull;&bull;&bull;&nbsp;&bull;&bull;&bull;&bull;&nbsp;&bull;&bull;&bull;&bull;</span>&nbsp;{customer.customer_context.finances.card.last_four_digits}
                        <br/>Udløber: {formatCardExpiration(customer.customer_context.finances.card.expiration)}
                    </span>
                </p>
                    <Link to={Routing.Selfcare.AddCard}>
                        Skift betalingskort
                    </Link></>
                :
                <Link to={Routing.Selfcare.AddCard}>
                    Tilføj betalingskort
                </Link>
            }</>
        }
    </div>
}

function ProductPricingDetails({customer, productInstance}: { customer: Customer, productInstance: ProductInstance }) {
    if (isTheProductActiveUnknown(productInstance)) return;

    if (isLargeOrGrossSalaryCustomer(customer)) return;

    if ([PRODUCT_STATUS.FINISH, PRODUCT_STATUS.CANCEL].includes(productInstance.product_instance_status)) return;

    return <>
        <ChargePeriods charge_periods={productInstance.charge_periods}/>
        <ChargePeriodsDetails productInstance={productInstance}/>

        {productInstance.product_instance_mandatory_limit_date && new Date(productInstance.product_instance_mandatory_limit_date) > new Date() &&
            <p className="sub">
                Bindingsperiode udløber d. {formatDate(productInstance.product_instance_mandatory_limit_date)}
            </p>
        }
    </>
}

function ExtenderProductPricingDetails({customer, productInstances}: { customer: Customer, productInstances: ProductInstance[] }) {
    if (productInstances.some(productInstance => isTheProductActiveUnknown(productInstance))) {
        return;
    }
    if (isLargeOrGrossSalaryCustomer(customer)) {
        return;
    }

    const current_period: ChargePeriod[] = productInstances.map(
        (productInstance: ProductInstance) => productInstance.charge_periods[0],
    );
    const current_total = current_period.reduce((total, charge_period) => total + charge_period.amount_total, 0);

    return <p><strong>Pris: </strong>{formatAmountWithCurrencyOverPeriod(current_total)}</p>;
}

function Period({charge_period}: { charge_period: ChargePeriod }) {
    return <span>{" "}
        {isDateMaxDate(charge_period.end_date)
            ? <>fra d. <time>{formatDate(charge_period.start_date)}</time></>
            : <>d. <time>{formatDate(charge_period.start_date)}</time> &ndash;
                <time>{formatDate(charge_period.end_date)}</time>
            </>
        }
    </span>;
}

function PeriodFirst({charge_period}: { charge_period: ChargePeriod }) {
    return <span>
        {isDateMaxDate(charge_period.end_date)
            ? <time>{formatDate(charge_period.start_date)}</time>
            : <>
                <time>{formatDate(charge_period.start_date)}</time>
                &ndash;
                <time>{formatDate(charge_period.end_date)}</time>
            </>
        }
    </span>;
}

function Price({charge_period, shouldDisplayPeriod}: { charge_period: ChargePeriod, shouldDisplayPeriod: boolean }) {
    const total = formatAmountWithCurrencyOverPeriod(charge_period.amount_total + calculateDiscountTotal(charge_period));
    return <p className={'price'}>
        {charge_period.discount_instances.length > 0
            ? <><strong>Pris:</strong> {total}</>
            : <><strong>Pris:</strong> {total}</>} {shouldDisplayPeriod && <>i perioden <PeriodFirst charge_period={charge_period}></PeriodFirst></>}
        <br/>
        {charge_period.discount_instances.length > 0 && <><strong>Rabat:</strong>{' '}
            <span className={'discount-total'}><strong>{formatAmountWithCurrencyOverPeriod(calculateDiscountTotal(charge_period))}</strong></span></>}
    </p>;
}

function SubPrice({charge_period}: { charge_period: ChargePeriod }) {
    const total = formatAmountWithCurrencyOverPeriod(charge_period.amount_total + calculateDiscountTotal(charge_period));
    return <p className={'sub'}>
        <span>{total}</span>
        <Period charge_period={charge_period}></Period>
    </p>;
}

function ChargePeriods({charge_periods}: {charge_periods: ChargePeriod[]}) {
    return <>
        {charge_periods.map((charge_period: ChargePeriod, index: number) => {
            if (index === 0) {
                return <Price
                    key={index}
                    shouldDisplayPeriod={charge_periods.length > 1}
                    charge_period={charge_period}
                ></Price>;
            }
            return <SubPrice key={index} charge_period={charge_period}></SubPrice>
        })}
    </>;
}

function ChargePeriodsDetails({productInstance}: { productInstance: ProductInstance }) {
    const detailsState = useDetailsState({open: false});
    const currentChargePeriodTotalDiscount = calculateDiscountTotal(productInstance.charge_periods[0]);
    const currentChargePeriodHasDiscount = currentChargePeriodTotalDiscount < 0;

    if (!hasProductInstanceOneOrMoreDiscounts(productInstance)) {
        return;
    }

    return <Details {...detailsState} className={'pricing-details'}>
        <summary><i></i>Prisoversigt</summary>
        {currentChargePeriodHasDiscount && <div className='discount-total'>
            <p>Samlet rabat lige nu</p>
            <p><strong>{formatAmountWithCurrencyOverPeriod(currentChargePeriodTotalDiscount)}</strong></p>
        </div>}
        {productInstance.charge_periods.map((charge_period: ChargePeriod) => <ChargePeriodsDetailsTable charge_period={charge_period}/>)}
    </Details>;
}

function ChargePeriodsDetailsTable({charge_period}: { charge_period: ChargePeriod }) {
    return <table className='summary-table'>
        <thead>
        <tr>
            {isDateMaxDate(charge_period.end_date)
                ? <th colSpan={2}>Fra d. {formatDate(charge_period.start_date)}</th>
                : <th colSpan={2}>D. {formatDate(charge_period.start_date)} &ndash; {formatDate(charge_period.end_date)}</th>
            }
        </tr>
        </thead>
        <tbody>
        <tr>
            <td>Normalpris</td>
            <td>{formatAmountWithCurrencyOverPeriod(charge_period.amount_total)}</td>
        </tr>
        {charge_period.discount_instances.map((discount_instance: DiscountInstance, index: number) =>
            <ChargePeriodsDetailsTableRow discount_instance={discount_instance} charge_period={charge_period} key={index}/>
        )}
        </tbody>
        <tfoot>
        <tr className='total-row'>
            <td>Pris i alt</td>
            <td>{formatAmountWithCurrencyOverPeriod(charge_period.amount_total + calculateDiscountTotal(charge_period))}</td>
        </tr>
        {hasChargePeriodOneOrMoreDiscounts(charge_period) &&
            <tr className='total-row'>
                <td>Du sparer</td>
                <td className='discount'>{formatAmountWithCurrencyOverPeriod(-calculateDiscountTotal(charge_period))}</td>
            </tr>
        }
        </tfoot>
    </table>;
}

function ChargePeriodsDetailsTableRow({discount_instance, charge_period}: { discount_instance: DiscountInstance, charge_period: ChargePeriod }) {
    if (isDiscountInstanceMonetary(discount_instance)) {
        return <ChargePeriodsDetailsTableRowMonetary discount_instance={discount_instance as DiscountInstanceMonetary}/>;
    }

    return <ChargePeriodsDetailsTableRowPercentage
        discount_instance={discount_instance as DiscountInstancePercentage}
        amount_total={charge_period.amount_total}
    />;
}

function ChargePeriodsDetailsTableRowMonetary({discount_instance}: { discount_instance: DiscountInstanceMonetary }) {
    return <tr>
        <td>{discount_instance.discount_instance_invoice_line_display}</td>
        <td className='discount'>{formatAmountWithCurrencyOverPeriod(-discount_instance.discount_instance_amount_base + -discount_instance.discount_instance_amount_vat)}</td>
    </tr>;
}

function ChargePeriodsDetailsTableRowPercentage({discount_instance, amount_total}: {
    discount_instance: DiscountInstancePercentage,
    amount_total: number
}) {
    return <tr>
        <td>
            {discount_instance.discount_instance_invoice_line_display}<br/>
            <small>{"(-"}{discount_instance.discount_instance_percentage}{"%/md.)"}</small>
        </td>
        <td className='discount'>{formatAmountWithCurrencyOverPeriod(amount_total / 100 * -discount_instance.discount_instance_percentage)}</td>
    </tr>;
}

function isLargeOrGrossSalaryCustomer(customer: Customer): boolean {
    return [
        CUSTOMER_TYPES.END_CUSTOMER_LARGE_ACCOUNT,
        CUSTOMER_TYPES.END_CUSTOMER_GROSS_SALARY,
    ].includes(customer.customer_type.customer_type_id);
}

function isLargeCustomer(customer: Customer): boolean {
    return [CUSTOMER_TYPES.END_CUSTOMER_LARGE_ACCOUNT].includes(customer.customer_type.customer_type_id)
}

function isExtenderProduct(productInstance: ProductInstance): boolean {
    return [PRODUCT_IDS.EXTENDER].includes(productInstance.product.product_id)
}

function isCustomerClosed(customer: Customer): boolean {
    return [CUSTOMER_STATUS.FINISH, CUSTOMER_STATUS.CANCEL].includes(customer.customer_context.customer_status);
}

function calculateDiscountTotal(charge_period: ChargePeriod): number {
    return -charge_period.discount_instances.reduce((total, discount_instance) => {

        if (isDiscountInstanceMonetary(discount_instance)) {
            return total + (discount_instance as DiscountInstanceMonetary).discount_instance_amount_base + (discount_instance as DiscountInstanceMonetary).discount_instance_amount_vat;
        } else {
            return total + charge_period.amount_total / 100 * (discount_instance as DiscountInstancePercentage).discount_instance_percentage;
        }

    }, 0)
}

function hasProductInstanceOneOrMoreDiscounts(productInstance: ProductInstance): boolean {
    return productInstance.charge_periods.some(charge_period => hasChargePeriodOneOrMoreDiscounts(charge_period))
}

function hasChargePeriodOneOrMoreDiscounts(charge_period: ChargePeriod): boolean {
    return charge_period.discount_instances.length > 0
}

function isDiscountInstanceMonetary(discount_instance: DiscountInstance): boolean {
    return discount_instance.discount_instance_amount_base !== null
           && discount_instance.discount_instance_amount_vat !== null
}

function isTheProductActiveKnown(productInstance: ProductInstance): boolean {
    return productInstance.product_instance_status_active_date !== null
           && !isDateMaxDate(productInstance.product_instance_status_active_date);
}

function isTheProductActiveUnknown(productInstance: ProductInstance): boolean {
    return !isTheProductActiveKnown(productInstance)
}

function isProductFinishedWithDate(productInstance: ProductInstance): boolean {
    return productInstance.product_instance_status === PRODUCT_STATUS.FINISH
           && productInstance.product_instance_status_pending_finish_date !== null
           && isDateMaxDate(productInstance.product_instance_status_pending_finish_date) !== true
           && productInstance.product_instance_status_finish_date !== null
           && isDateMaxDate(productInstance.product_instance_status_finish_date) !== true;
}

function isProductCancelledWithDate(productInstance: ProductInstance): boolean {
    return productInstance.product_instance_status === PRODUCT_STATUS.CANCEL
           && productInstance.product_instance_status_cancel_date !== null
           && isDateMaxDate(productInstance.product_instance_status_cancel_date) !== true;
}

function isPendingFinished(productInstance: ProductInstance): boolean {
    return productInstance.product_instance_status_pending_finish_date !== null
           && isDateMaxDate(productInstance.product_instance_status_pending_finish_date) !== true;
}

function isProductIpProduct(productInstance: ProductInstance): boolean {
    return [PRODUCT_IDS.STATIC_IP, PRODUCT_IDS.DYNAMIC_IP].includes(productInstance.product.product_id);
}
function isProductNotIpProduct(productInstance: ProductInstance): boolean {
    return !isProductIpProduct(productInstance);
}

function CustomerClosed({customer}: { customer: Customer }) {
    if (isCustomerClosed(customer) === false) {
        return;
    }
    return <section className='selfcare-account-closed-notification'>
        <h2 className={'heading heading--montserrat'}>Dette abonnement er lukket</h2>
    </section>;
}
